"use client";

import { useEffect, useState } from "react";
import { getApps, initializeApp, FirebaseApp, FirebaseOptions, getApp } from "firebase/app";
import { RemoteConfig, getRemoteConfig, fetchAndActivate, getValue } from "firebase/remote-config";
import { getMessaging, Messaging } from "firebase/messaging";

// 🔑 Firebase configuration using environment variables or hardcoded (prefer env in production)
const firebaseConfig: FirebaseOptions = {
    apiKey: "AIzaSyBSegBn-VT7Ozm7VGCijQcfFubTflkpsFQ",
    authDomain: "incredunlisted---mobileapp.firebaseapp.com",
    projectId: "incredunlisted---mobileapp",
    storageBucket: "incredunlisted---mobileapp.firebasestorage.app",
    messagingSenderId: "162046387945",
    appId: "1:162046387945:web:d56d32f6983fb9bd1ff278",
    measurementId: "G-D9D09RD549",
    databaseURL: "https://incredunlisted---mobileapp-default-rtdb.firebaseio.com/",
};

let app: FirebaseApp | null = null;
let remoteConfig: RemoteConfig | null = null;
let messaging: Messaging | null = null;

/**
 * Initializes Firebase and Remote Config if not already initialized
 * Only runs on client-side to avoid SSR issues
 */
export const initializeFirebase = (): {
    app: FirebaseApp;
    remoteConfig: RemoteConfig;
    messaging: Messaging;
} => {
    if (typeof window === "undefined") {
        throw new Error("Firebase can only be initialized in the client");
    }

    // Reuse existing Firebase app if it's already initialized
    if (!app) {
        if (getApps().length === 0) {
            app = initializeApp(firebaseConfig);
        } else {
            app = getApp();
        }

        remoteConfig = getRemoteConfig(app);
        remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
        messaging = getMessaging(app);
    }

    if (!app || !remoteConfig || !messaging) {
        throw new Error("Failed to initialize Firebase services");
    }

    return { app, remoteConfig, messaging };
};

/**
 * Custom hook to fetch and manage remote config values from Firebase
 * @param key - The key of the remote config parameter to fetch
 * @param defaultValue - Default value to use while loading or if fetch fails
 * @returns Object containing:
 *  - data: The fetched data of type T
 *  - loading: Boolean indicating if fetch is in progress
 *  - error: Error message if fetch fails, null otherwise
 */
export function useRemoteConfig<T>(
    key: string,
    defaultValue: T,
): {
    data: T;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<T>(defaultValue);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Initialize or reuse Firebase instance
                const { remoteConfig } = initializeFirebase();

                // Fetch and activate the latest remote config values
                await fetchAndActivate(remoteConfig);
                const remoteValue = getValue(remoteConfig, key);

                let parsedData: T;

                // Handle different types of remote config values
                if (remoteValue.asString() === "") {
                    console.warn(`Remote config value for "${key}" is empty`);
                    parsedData = defaultValue;
                } else {
                    parsedData = JSON.parse(remoteValue.asString()) as T;
                }

                setData(parsedData);
                setError(null);
            } catch (err) {
                console.error(`Error fetching remote config for ${key}:`, err);
                setError(`Failed to load remote config for "${key}"`);
                setData(defaultValue);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [key]);

    return { data, loading, error };
}
